import React, { useState, useEffect } from 'react'
import './MealmateSection.css'
import { Container } from '../ui/Container'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { 
  ChefHat, 
  Search,
  Heart,
  ShoppingCart,
  Users,
  Leaf,
  Timer,
  Star,
  Plane
} from 'lucide-react'
import { mealmateService, Vendor, Meal } from '../../services/mealmateService'
import { useAppStore } from '../../store'
import VendorCard from './VendorCard'
import MealCard from './MealCard'
import DietaryPreferencesModal from './DietaryPreferencesModal'
import VendorDetailsModal from './VendorDetailsModal'
import TravelModeBanner from './TravelModeBanner'
import TravelCoordinationModal from './TravelCoordinationModal'
import AIVisual from '../ui/AIVisual'

const MealmateSection: React.FC = () => {
  const { user, isAuthenticated } = useAppStore()
  const [vendors, setVendors] = useState<Vendor[]>([])
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null)
  const [vendorMeals, setVendorMeals] = useState<Meal[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [dietaryFilters, setDietaryFilters] = useState<string[]>([])
  const [showDietaryModal, setShowDietaryModal] = useState(false)
  const [showVendorModal, setShowVendorModal] = useState(false)
  const [cartItems, setCartItems] = useState<{meal: Meal, quantity: number}[]>([])
  const [showCart, setShowCart] = useState(false)
  const [showTravelModal, setShowTravelModal] = useState(false)
  const [travelMode, setTravelMode] = useState(user?.travel_mode || false)

  const categories = [
    { id: 'all', name: 'Curated Selection', icon: ChefHat },
    { id: 'healthy', name: 'Vegan & Organic', icon: Leaf },
    { id: 'quick', name: 'Artisanal Express', icon: Timer },
    { id: 'family', name: 'Family Heritage', icon: Users },
    { id: 'travel', name: 'Elite Hospitality', icon: Plane },
    { id: 'trending', name: 'Member Favorites', icon: Star }
  ]

  useEffect(() => {
    loadVendors()
    loadUserDietaryPreferences()
  }, [])

  useEffect(() => {
    if (selectedVendor) {
      loadVendorMeals(selectedVendor.id)
    }
  }, [selectedVendor])

  const loadVendors = async () => {
    try {
      setLoading(true)
      const response = await mealmateService.getVendors({
        dietary: dietaryFilters,
        page: 1,
        limit: 20
      })
      
      if (response.data?.items) {
        setVendors(response.data.items)
      }
    } catch (error) {
      console.error('Error loading vendors:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadUserDietaryPreferences = async () => {
    if (!isAuthenticated || !user) return
    
    try {
      const response = await mealmateService.getUserDietaryPreferences(user.id)
      if (response.data) {
        setDietaryFilters(response.data.restrictions || [])
      }
    } catch (error) {
      console.error('Error loading dietary preferences:', error)
    }
  }

  const loadVendorMeals = async (vendorId: string) => {
    try {
      const response = await mealmateService.getVendorMeals(vendorId, {
        dietary: dietaryFilters,
        available: true
      })
      
      if (response.data?.items) {
        setVendorMeals(response.data.items)
      }
    } catch (error) {
      console.error('Error loading vendor meals:', error)
    }
  }

  const filteredVendors = vendors.filter(vendor => {
    const matchesSearch = vendor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         vendor.description.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesCategory = selectedCategory === 'all' || 
                           (selectedCategory === 'healthy' && vendor.dietaryOptions.some(d => ['Organic', 'Healthy'].includes(d))) ||
                           (selectedCategory === 'quick' && vendor.deliveryTime.includes('30')) ||
                           (selectedCategory === 'travel' && vendor.cuisineTypes.some(c => ['Flight', 'Hotel', 'Hospitality'].includes(c))) ||
                           (selectedCategory === 'family' && vendor.cuisineTypes.includes('Family')) ||
                           (selectedCategory === 'trending' && vendor.rating >= 4.5)
    
    return matchesSearch && matchesCategory
  })

  const openTravelCoordination = (vendor: Vendor) => {
    setSelectedVendor(vendor)
    setShowTravelModal(true)
  }

  const addToCart = (meal: Meal, quantity: number = 1) => {
    setCartItems(prev => {
      const existingItem = prev.find(item => item.meal.id === meal.id)
      if (existingItem) {
        return prev.map(item => 
          item.meal.id === meal.id 
            ? { ...item, quantity: item.quantity + quantity }
            : item
        )
      }
      return [...prev, { meal, quantity }]
    })
  }

  const removeFromCart = (mealId: string) => {
    setCartItems(prev => prev.filter(item => item.meal.id !== mealId))
  }

  const updateCartQuantity = (mealId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(mealId)
      return
    }
    
    setCartItems(prev => 
      prev.map(item => 
        item.meal.id === mealId 
          ? { ...item, quantity }
          : item
      )
    )
  }

  const getTotalCartValue = () => {
    return cartItems.reduce((total, item) => total + (item.meal.price * item.quantity), 0)
  }

  const getTotalCartItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0)
  }

  if (loading) {
    return (
      <Container className="py-12">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-spin-slow">
              <ChefHat className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-theme-text-primary mb-2">Loading Partner Vendors</h3>
            <p className="text-theme-text-secondary">Connecting you with the best meal partners...</p>
          </div>
        </div>
      </Container>
    )
  }

  return (
    <div className="min-h-screen bg-theme-bg theme-transition">
      {isAuthenticated && (
        <TravelModeBanner 
          initialMode={travelMode} 
          onToggle={(enabled) => setTravelMode(enabled)} 
        />
      )}
      {/* How It Works Section */}
      <div className="bg-gradient-to-br from-theme-bg to-theme-surface py-16">
        <Container className="text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-theme-text mb-4">The Culinary Journey</h2>
          <p className="text-xl text-theme-text-muted max-w-2xl mx-auto mb-12">
            A three-step symphony of biological optimization and gastronomic excellence.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-theme-primary to-theme-secondary rounded-full flex items-center justify-center group transition-all duration-300 hover:scale-110 hover:shadow-lg">
                <ChefHat className="w-8 h-8 text-white drop-shadow-lg transition-all duration-300 group-hover:rotate-12 group-hover:scale-110 icon-visibility-enhanced" />
              </div>
              <h3 className="text-xl font-bold text-theme-text mb-4">Artisanal Selection</h3>
              <p className="text-theme-text-muted">
                Choose from a curated collection of organic, vegan-forward, and bio-synced meals, hand-picked for their nutrient density and seasonal purity.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-theme-primary to-theme-secondary rounded-full flex items-center justify-center group transition-all duration-300 hover:scale-110 hover:shadow-lg">
                <Timer className="w-8 h-8 text-white drop-shadow-lg transition-all duration-300 group-hover:rotate-12 group-hover:scale-110 icon-visibility-enhanced" />
              </div>
              <h3 className="text-xl font-bold text-theme-text mb-4">White-Glove Fulfillment</h3>
              <p className="text-theme-text-muted">
                Our culinary partners execute with precision, ensuring every ingredient is carefully picked and prepared to preserve its biological integrity.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-theme-primary to-theme-secondary rounded-full flex items-center justify-center group transition-all duration-300 hover:scale-110 hover:shadow-lg">
                <Heart className="w-8 h-8 text-white drop-shadow-lg transition-all duration-300 group-hover:rotate-12 group-hover:scale-110 icon-visibility-enhanced" />
              </div>
              <h3 className="text-xl font-bold text-theme-text mb-4">Enjoy Instantly</h3>
              <p className="text-theme-text-muted">
                Heat and enjoy in just minutes. No cooking. No mess. No hassle.
              </p>
            </div>
          </div>
        </Container>
      </div>

      {/* Clean Ingredients Section */}
      <div className="bg-theme-surface py-16">
        <Container className="text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-theme-text mb-4">Purity Standards</h2>
          <p className="text-xl text-theme-text-muted max-w-2xl mx-auto mb-12">
            We curate for the biology of excellence. Our strict selection criteria prioritize organic produce and regenerative farming.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="text-center p-8 bg-theme-elevated rounded-2xl border border-theme-border">
              <div className="w-16 h-16 mx-auto mb-6 bg-theme-error rounded-full flex items-center justify-center">
                <div className="w-8 h-8 bg-theme-text-inverse rounded-full flex items-center justify-center">
                  <span className="text-theme-error font-bold">🌾</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-theme-text mb-4">Gluten-Free Excellence</h3>
              <p className="text-theme-text-muted">
                We eliminate systemic inflammation by sourcing only certified gluten-free, ancient grains and organic alternatives that respect your gut biome.
              </p>
            </div>
            
            <div className="text-center p-8 bg-theme-elevated rounded-2xl border border-theme-border">
              <div className="w-16 h-16 mx-auto mb-6 bg-theme-warning rounded-full flex items-center justify-center">
                <div className="w-8 h-8 bg-theme-text-inverse rounded-full flex items-center justify-center">
                  <span className="text-theme-warning font-bold">🥛</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-theme-text mb-4">Dairy-Free Innovation</h3>
              <p className="text-theme-text-muted">
                Experience creamy textures through artisanal plant-based infusions. We omit dairy to optimize digestive transit and skin clarity.
              </p>
            </div>
            
            <div className="text-center p-8 bg-theme-elevated rounded-2xl border border-theme-border">
              <div className="w-16 h-16 mx-auto mb-6 bg-theme-warning rounded-full flex items-center justify-center">
                <div className="w-8 h-8 bg-theme-text-inverse rounded-full flex items-center justify-center">
                  <span className="text-theme-warning font-bold">🍯</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-theme-text mb-4">Zero Refined Sugars</h3>
              <p className="text-theme-text-muted">
                Sweetness, evolved. We use only low-glycemic, whole-food sweeteners to maintain metabolic stability and prevent insulin spikes.
              </p>
            </div>
          </div>
        </Container>
      </div>

      {/* Header */}
      <div className="bg-theme-surface light-surface border-b border-theme-border theme-transition">
        <Container className="py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl flex items-center justify-center">
                <ChefHat className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-theme-primary theme-transition">The Lab-to-Table Experience</h1>
                <p className="text-theme-secondary theme-transition">Organic, vegan-focused, and biologically optimized meals</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                onClick={() => setShowDietaryModal(true)}
                className="border-green-600 text-green-600 hover:bg-green-50 theme-transition"
              >
                <Heart className="w-4 h-4 mr-2" />
                Dietary Preferences
                {dietaryFilters.length > 0 && (
                  <Badge variant="secondary" className="ml-2 bg-green-100 text-green-800 theme-transition">
                    {dietaryFilters.length}
                  </Badge>
                )}
              </Button>
              
              <Button
                onClick={() => setShowCart(true)}
                className="relative bg-green-600 hover:bg-green-700"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Cart
                {getTotalCartItems() > 0 && (
                  <Badge className="absolute -top-2 -right-2 bg-red-500 text-white text-xs">
                    {getTotalCartItems()}
                  </Badge>
                )}
              </Button>
            </div>
          </div>
        </Container>
      </div>

      {/* Filters and Search */}
      <Container className="py-8">
        <div className="bg-theme-surface light-surface rounded-2xl p-6 shadow-sm border border-theme-border theme-transition mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-theme-text-muted" />
                <input
                  type="text"
                  placeholder="Search vendors or cuisines..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-theme-border bg-theme-surface text-theme-text-primary rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent theme-transition"
                />
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => {
                const Icon = category.icon
                return (
                  <Button
                    key={category.id}
                    variant={selectedCategory === category.id ? 'default' : 'outline'}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`${
                      selectedCategory === category.id
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'border-theme-border text-theme-primary hover:bg-theme-surface theme-transition'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {category.name}
                  </Button>
                )
              })}
            </div>
          </div>
        </div>

        {/* Vendor Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredVendors.map((vendor) => (
            <VendorCard
              key={vendor.id}
              vendor={vendor}
              onSelect={() => {
                setSelectedVendor(vendor)
                setShowVendorModal(true)
              }}
              onQuickView={() => setSelectedVendor(vendor)}
              onCoordinate={selectedCategory === 'travel' ? () => openTravelCoordination(vendor) : undefined}
            />
          ))}
        </div>

        {filteredVendors.length === 0 && (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-theme-surface rounded-full flex items-center justify-center mx-auto mb-4">
              <ChefHat className="w-12 h-12 text-theme-muted" />
            </div>
            <h3 className="text-xl font-semibold text-theme-primary theme-transition mb-2">No vendors found</h3>
            <p className="text-theme-secondary theme-transition">Try adjusting your search or filters</p>
          </div>
        )}
      </Container>

      {/* Selected Vendor Meals */}
      {selectedVendor && (
        <Container className="py-8">
          <div className="bg-theme-surface light-surface rounded-2xl p-8 shadow-sm border border-theme-border theme-transition">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-theme-primary theme-transition mb-2">{selectedVendor.name} Menu</h2>
                <p className="text-theme-secondary theme-transition">{selectedVendor.description}</p>
              </div>
              <Button
                variant="outline"
                onClick={() => setSelectedVendor(null)}
                className="border-theme-border text-theme-primary hover:bg-theme-surface theme-transition"
              >
                View All Vendors
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {vendorMeals.map((meal) => (
                <MealCard
                  key={meal.id}
                  meal={meal}
                  onAddToCart={addToCart}
                  dietaryFilters={dietaryFilters}
                />
              ))}
            </div>
          </div>
        </Container>
      )}

      {/* Modals */}
      {showDietaryModal && (
        <DietaryPreferencesModal
          isOpen={showDietaryModal}
          onClose={() => setShowDietaryModal(false)}
          currentPreferences={dietaryFilters}
          onSave={async (preferences) => {
            setDietaryFilters(preferences)
            if (isAuthenticated && user) {
              await mealmateService.updateUserDietaryPreferences(user.id, {
                restrictions: preferences,
                allergies: [],
                preferences: []
              })
            }
            await loadVendors()
          }}
        />
      )}

      {showVendorModal && selectedVendor && (
        <VendorDetailsModal
          isOpen={showVendorModal}
          onClose={() => setShowVendorModal(false)}
          vendor={selectedVendor}
          onSelectMeals={loadVendorMeals}
        />
      )}

      <TravelCoordinationModal
        isOpen={showTravelModal}
        onClose={() => setShowTravelModal(false)}
        partnerId={selectedVendor?.id}
        partnerName={selectedVendor?.name}
      />

      {/* Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowCart(false)} />
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-theme-surface shadow-xl theme-transition">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between p-6 border-b border-theme-border theme-transition">
                <h3 className="text-lg font-semibold text-theme-primary theme-transition">Your Cart</h3>
                <Button
                  variant="ghost"
                  onClick={() => setShowCart(false)}
                  className="text-theme-muted hover:text-theme-primary theme-transition"
                >
                  ×
                </Button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6">
                {cartItems.length === 0 ? (
                  <div className="text-center py-8">
                    <ShoppingCart className="w-16 h-16 text-theme-muted mx-auto mb-4" />
                    <p className="text-theme-secondary theme-transition">Your cart is empty</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {cartItems.map((item) => (
                      <div key={item.meal.id} className="flex items-center space-x-4 p-4 bg-theme-surface rounded-lg theme-transition">
                        <AIVisual
                          src={item.meal.image}
                          alt={item.meal.name}
                          fallbackType="food"
                          aspectRatio="square"
                          className="w-16 h-16 rounded-lg"
                        />
                        <div className="flex-1">
                          <h4 className="font-medium text-theme-primary theme-transition">{item.meal.name}</h4>
                          <p className="text-sm text-theme-secondary theme-transition">${item.meal.price}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateCartQuantity(item.meal.id, item.quantity - 1)}
                            className="w-8 h-8 p-0"
                          >
                            -
                          </Button>
                          <span className="w-8 text-center">{item.quantity}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateCartQuantity(item.meal.id, item.quantity + 1)}
                            className="w-8 h-8 p-0"
                          >
                            +
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {cartItems.length > 0 && (
                <div className="border-t border-theme-border theme-transition p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-lg font-semibold text-theme-primary theme-transition">Total</span>
                    <span className="text-lg font-bold text-green-600">${getTotalCartValue().toFixed(2)}</span>
                  </div>
                  <Button className="w-full bg-green-600 hover:bg-green-700">
                    Proceed to Checkout
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MealmateSection
